import { useState, useEffect, useCallback } from 'react';
import { db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useBadgeData } from './BadgeUtils';
import { themes } from './constants';
import { normalizeTheme } from './utils';
import { defaultLearnScore, defaultStats, defaultUserData } from './defaults';

export const useAccountData = (user) => {
  const [userData, setUserData] = useState(null);
  const [learnData, setLearnData] = useState(null);
  const [activitySummary, setActivitySummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const formatDate = useCallback((date) => date.toISOString().split('T')[0], []);

  const generateActivitySummary = useCallback((data, learnData) => {
    const summary = {
      dailyActivity: {},
      totalStats: {
        quizCount: data.totalQuizzes || data.stats?.totalQuizzes || 0,
        flashcardsStudied: data.stats?.flashcardsStudied || data.flashcardsStudied || 0,
        bestScores: data.bestScores || {},
        quizByCategory: { Divers: 0, Géographie: 0, Histoire: 0, Science: 0, Nature: 0, Art: 0 },
        flashcardByCategory: { Divers: 0, Géographie: 0, Histoire: 0, Science: 0, Nature: 0, Art: 0 },
        badges: [...new Set([...(data.stats?.badges || []), ...(data.badges || [])])],
        boxProgressionByTheme: {},
        quizPerformanceByTheme: {},
        timeSpent: 0,
        sessionHistory: data.sessionHistory || [],
      },
    };

    const mainThemes = [
      "Divers",
      'Géographie',
      'Histoire',
      'Art',
      'Littérature et Langues',
      'Culture Pop',
      'Science et Nature',
      'Sport'
    ];
    const themeMapping = {
      'Capitale': 'Géographie',
      'Drapeau': 'Géographie',
      'Monnaies': 'Géographie',      
      'Football': 'Sport',
      'Formule 1': 'Sport',
      'Littérature': 'Littérature et Langues',
      'Prix Goncourt': 'Littérature et Langues',
      'Nature': 'Science et Nature',
      'Astronomie': 'Science et Nature',
      'Prix Nobel': 'Science et Nature',
      'Prix Turing': 'Science et Nature',
      'Médaille Fields': 'Science et Nature',
      'Cinéma': 'Culture Pop',
      'Musique': 'Culture Pop',
      'Fort Boyard': 'Culture Pop',
      'Architecture': 'Art',
      'Sculpture': 'Art',
      'Peinture': 'Art'
    };

    const normalizeCategory = (category) => {
      if (category === 'Geographie') return 'Géographie';
      return category;
    };

    const mapToMainTheme = (theme) => {
      return themeMapping[theme] || (mainThemes.includes(theme) ? theme : "Divers");
    };

    const now = new Date();
    const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());

    Object.keys(data.activity?.daily || {}).forEach((date) => {
      const parsedDate = new Date(date);
      if (parsedDate >= oneYearAgo) {
        const dailyData = data.activity.daily[date];
        summary.dailyActivity[date] = {
          quizCount: dailyData.quizzes || 0,
          flashcardsStudied: dailyData.flashcards || 0,
          timeSpent: dailyData.timeSpent || 0,
        };
        summary.totalStats.timeSpent += dailyData.timeSpent || 0;
      }
    });

    Object.keys(data.dailyFlashcards || {}).forEach((date) => {
      const parsedDate = new Date(date);
      if (parsedDate >= oneYearAgo) {
        const flashData = data.dailyFlashcards[date];
        summary.dailyActivity[date] = summary.dailyActivity[date] || { quizCount: 0, flashcardsStudied: 0, timeSpent: 0 };
        summary.dailyActivity[date].flashcardsStudied += flashData.total || 0;
        Object.entries(flashData).forEach(([category, count]) => {
          const normalizedCategory = normalizeCategory(category);
          const mainTheme = mapToMainTheme(normalizedCategory);
          summary.totalStats.flashcardByCategory[mainTheme] = (summary.totalStats.flashcardByCategory[mainTheme] || 0) + count;
        });
      }
    });

    Object.entries(learnData || {}).forEach(([flashcardId, { box, themes: flashcardThemes }]) => {
      (flashcardThemes || []).forEach((theme) => {
        const normalizedTheme = normalizeCategory(theme);
        if (!summary.totalStats.boxProgressionByTheme[normalizedTheme]) {
          summary.totalStats.boxProgressionByTheme[normalizedTheme] = {};
        }
        summary.totalStats.boxProgressionByTheme[normalizedTheme][flashcardId] = box;
      });
    });

    return summary;
  }, []);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user || !user.uid) {
        setLoading(false);
        return;
      }

      try {
        // Check local storage
        const cachedData = localStorage.getItem(`learnData_${user.uid}`);
        if (cachedData) {
          const { userData: cachedUserData, learnData: cachedLearnData } = JSON.parse(cachedData);
          const hasValidLearnScore = cachedUserData.learnScore && themes.every(theme => cachedUserData.learnScore[normalizeTheme(theme)] !== undefined);
          const hasValidStats = cachedUserData.stats && typeof cachedUserData.stats.flashcardsStudied === 'number';
          if (
            cachedUserData.sessionHistory &&
            cachedUserData.sessionHistory.length > 0 &&
            hasValidLearnScore &&
            hasValidStats
          ) {
            console.log('Utilisation de learnData depuis localStorage');
            setUserData({
              ...defaultUserData,
              ...cachedUserData,
              learnScore: { ...defaultLearnScore, ...cachedUserData.learnScore },
              stats: { ...defaultStats, ...cachedUserData.stats }
            });
            setLearnData(cachedLearnData);
            setActivitySummary(generateActivitySummary(cachedUserData, cachedLearnData));
            setLoading(false);
            return;
          } else {
            console.warn('Données en cache invalides ou incomplètes (learnScore ou stats manquant ou incomplet), récupération depuis Firestore');
          }
        }

        // Fetch userData from Firestore
        console.log('Récupération de userData depuis Firestore');
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);

        let fetchedUserData = defaultUserData;
        if (userDocSnap.exists()) {
          const data = userDocSnap.data();
          fetchedUserData = {
            ...defaultUserData,
            ...data,
            learnScore: { ...defaultLearnScore, ...data.learnScore },
            stats: { ...defaultStats, ...data.stats },
            activity: data.activity || defaultUserData.activity,
            dailyFlashcards: data.dailyFlashcards || defaultUserData.dailyFlashcards,
            dailySessions: data.dailySessions || defaultUserData.dailySessions,
            totalSessionsByTheme: data.totalSessionsByTheme || defaultUserData.totalSessionsByTheme,
            totalFlashcardsByTheme: data.totalFlashcardsByTheme || defaultUserData.totalFlashcardsByTheme,
            sessionHistory: data.sessionHistory || defaultUserData.sessionHistory,
            performance: data.performance || defaultUserData.performance,
          };
        }
        setUserData(fetchedUserData);

        // Fetch learnData from Firestore
        console.log('Récupération de learnData depuis Firestore');
        const collections = [
          'learndata0-2000',
          'learndata2000-4000',
          'learndata4000-6000',
          'learndata6000-8000',
          'learndata8000-10000',
          'learndata10000-12000',
          'learndata12000-14000',
          'learndata14000-16000',
          'learndata16000-18000',
          'learndata18000-20000',
          'learndata20000-22000',
        ];
        let fetchedLearnData = {};
        for (const collName of collections) {
          const docRef = doc(db, 'users', user.uid, collName, 'flashcards');
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            Object.assign(fetchedLearnData, data.flashcards || {});
          }
        }
        setLearnData(fetchedLearnData);

        // Generate activity summary
        const summary = generateActivitySummary(fetchedUserData, fetchedLearnData);
        setActivitySummary(summary);

        // Update localStorage with complete data
        localStorage.setItem(`learnData_${user.uid}`, JSON.stringify({ userData: fetchedUserData, learnData: fetchedLearnData }));
      } catch (error) {
        console.error('Erreur lors de la récupération des données utilisateur:', error);
        setUserData(defaultUserData);
        setLearnData({});
        setActivitySummary({ dailyActivity: {}, totalStats: defaultStats });
        localStorage.setItem(`learnData_${user.uid}`, JSON.stringify({ userData: defaultUserData, learnData: {} }));
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user, generateActivitySummary]);

  return { userData, learnData, activitySummary, loading, formatDate };
};

export const useStats = (activitySummary, userData, formatDate) => {
  const getStatsForTab = useCallback((tab) => {
    if (!activitySummary) return { quizCount: 0, flashcardsStudied: 0, timeSpent: 0 };
    const today = formatDate(new Date());
    const thisMonth = today.slice(0, 7);

    if (tab === "Aujourd'hui") {
      return activitySummary.dailyActivity[today] || { quizCount: 0, flashcardsStudied: 0, timeSpent: 0 };
    } else if (tab === 'Mois') {
      return Object.keys(activitySummary.dailyActivity)
        .filter(date => date.startsWith(thisMonth))
        .reduce((acc, date) => {
          const daily = activitySummary.dailyActivity[date];
          return {
            quizCount: acc.quizCount + (daily.quizCount || 0),
            flashcardsStudied: acc.flashcardsStudied + (daily.flashcardsStudied || 0),
            timeSpent: acc.timeSpent + (daily.timeSpent || 0),
          };
        }, { quizCount: 0, flashcardsStudied: 0, timeSpent: 0 });
    } else {
      return {
        ...activitySummary.totalStats,
        sessionHistory: userData?.sessionHistory || [],
      };
    }
  }, [activitySummary, userData, formatDate]);

  const calculateStreaks = useCallback(() => {
    if (!activitySummary || !userData) return { current: 0, longest: 0, streak10Count: 0 };
    const dates = Object.keys(activitySummary.dailyActivity).sort();
    let current = 0;
    let longest = 0;
    let streak10Count = 0;
    let prevDate = null;

    dates.forEach(date => {
      const currentDate = new Date(date);
      if (prevDate) {
        const diff = (currentDate - new Date(prevDate)) / (1000 * 60 * 60 * 24);
        if (diff === 1) {
          current += 1;
          if (current === 10) streak10Count += 1;
        } else if (diff > 1) {
          current = 1;
        }
      } else {
        current = 1;
      }
      longest = Math.max(longest, current);
      prevDate = date;
    });

    const today = formatDate(new Date());
    const lastStudyDate = userData.stats?.lastStudyDate;
    const finalCurrent = lastStudyDate === today ? current : (lastStudyDate === formatDate(new Date(Date.now() - 86400000)) ? current : 0);

    return { current: finalCurrent, longest, streak10Count };
  }, [activitySummary, userData, formatDate]);

  const getWeeklyProgressData = useCallback(() => {
    if (!activitySummary) return { labels: [], quizData: [], flashcardData: [] };
    const today = new Date();
    const labels = Array(7).fill(0).map((_, i) => {
      const d = new Date(today);
      d.setDate(today.getDate() - (6 - i));
      return d.toLocaleDateString('fr-FR', { weekday: 'short' });
    });
    const quizData = [];
    const flashcardData = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - (6 - i));
      const dateStr = formatDate(date);
      const daily = activitySummary.dailyActivity[dateStr] || { quizCount: 0, flashcardsStudied: 0 };
      quizData.push(daily.quizCount);
      flashcardData.push(daily.flashcardsStudied);
    }

    return { labels, quizData, flashcardData };
  }, [activitySummary, formatDate]);

  const getCategoryPerformance = useCallback(() => {
    if (!activitySummary || !userData || !userData.learnScore) {
      console.warn('Données manquantes pour getCategoryPerformance:', { activitySummary, userData });
      return { labels: [], data: [] };
    }

    const mainThemes = [
      "Divers",
      'Géographie',
      'Histoire',
      'Art',
      'Littérature et Langues',
      'Culture Pop',
      'Science et Nature',
      'Sport'
    ];
    const data = mainThemes.map((theme) => userData.learnScore[theme] || 0);

    return { labels: mainThemes, data };
  }, [activitySummary, userData]);

  const getBoxProgressionByCategory = useCallback((category) => {
    if (!activitySummary || !activitySummary.totalStats.boxProgressionByTheme) {
      return { labels: ['Boîte 1', 'Boîte 2', 'Boîte 3', 'Boîte 4', 'Boîte 5'], data: [0, 0, 0, 0, 0] };
    }

    const boxes = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    Object.entries(activitySummary.totalStats.boxProgressionByTheme).forEach(([theme, flashcards]) => {
      if (theme === category) {
        Object.values(flashcards).forEach((box) => {
          boxes[box] = (boxes[box] || 0) + 1;
        });
      }
    });

    return {
      labels: ['Boîte 1', 'Boîte 2', 'Boîte 3', 'Boîte 4', 'Boîte 5'],
      data: [boxes[1], boxes[2], boxes[3], boxes[4], boxes[5]],
    };
  }, [activitySummary]);

  const badgeDataHook = useBadgeData(activitySummary, userData, calculateStreaks, formatDate);

  return { getStatsForTab, calculateStreaks, getWeeklyProgressData, getCategoryPerformance, getBoxProgressionByCategory, getBadgeData: badgeDataHook };
};